import React, { useRef, useEffect, memo } from 'react'
import PropTypes from 'prop-types'
import { useRecoilValue } from 'recoil'
import { 
  contactData as contactDataState,
  siteData as siteDataState
} from '../../store/dataStore'
import { useAnimation } from 'framer-motion'
import CustomScroller from 'react-custom-scroller'

import Social from '../Social'
import {
  ContactSec,
  ContactWrapper,
  ContactTitle,
  ContactSections,
  ContactSection,
  ContactItemWrapper,
  People,
  Address,
  Email,
  Phone,
  Map,
  Copyright,
} from '../../styles/contactStyles.js'
import { Location } from '../../assets/svg/icons'

const ContactItem = memo(({ item }) => {
  return (
    <ContactItemWrapper>
      <h3>
        {item.name}
        <span>{item.position}</span>
      </h3>
      <a href={`mailto:${item.email}`}>{item.email}</a>
      <p dangerouslySetInnerHTML={{ __html: item.other }} />
    </ContactItemWrapper>
  )
})

const Contact = ({ visible }) => {
  const siteData = useRecoilValue(siteDataState)

  const contactData = useRecoilValue(contactDataState)
  const people = contactData.people
  const address = contactData.address

  const contactSection = useRef(null)
  const contactTitle = useRef(null)
  const contactSectionBox1 = useRef(null)
  const contactSectionBox2 = useRef(null)
  const contactCopy = useRef(null)
  const contactScroller = useRef(null)

  const contactSectionVars = {
    hidden: {
      visibility: 'hidden',
      zIndex: -1,
      opacity: 0,
    },
    visible: {
      visibility: 'visible',
      zIndex: 1,
      opacity: 1,
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.5,
        delay: 0.5,
        ease: 'linear',
      },
      transitionEnd: {
        visibility: 'hidden',
        zIndex: -1,
      },
    },
  }

  const contactTitleVars = {
    hidden: {
      y: '-80%',
      opacity: 0,
      transition: {
        duration: 1.5,
        ease: 'easeOut',
        delay: 0,
      },
    },
    visible: {
      y: 0,
      opacity: 1,
    },
  }

  const contactSect1Vars = {
    hidden: {
      opacity: 0,
      y: '20%',
      transition: {
        duration: 1.5,
        ease: 'easeOut',
        delay: 0,
      },
    },
    visible: { opacity: 1, y: 0 },
  }

  const contactSect2Vars = {
    hidden: {
      opacity: 0,
      y: '-20%',
      transition: {
        duration: 1.5,
        ease: 'easeOut',
        delay: 0,
      },
    },
    visible: { opacity: 1, y: 0 },
  }

  const contactCopyVars = {
    hidden: {
      opacity: 0,
      y: '100px',
      transition: {
        duration: 0.75,
        ease: 'easeOut',
        delay: 0,
      },
    },
    visible: { opacity: 1, y: 0 },
  }

  const contactSectionAnims = useAnimation(contactSectionVars)
  const contactTitleAnims = useAnimation(contactTitleVars)
  const contactBox1Anims = useAnimation(contactSect1Vars)
  const contactBox2Anims = useAnimation(contactSect2Vars)
  const contactCopyAnims = useAnimation(contactCopyVars)

  const copy = siteData.language === 'es' ? `Copyright © ${siteData.name} ${siteData.tagline} ${new Date().getFullYear()} | Desarrollado por Nerds ❤` : `Copyright © ${siteData.name} ${siteData.tagline} ${new Date().getFullYear()} | Developed by Nerds ❤`

  useEffect(() => {
    if (visible) {
      contactSectionAnims.start('visible')
      contactTitleAnims.start('visible')
      contactBox1Anims.start('visible')
      contactBox2Anims.start('visible')
      contactCopyAnims.start('visible')
      contactScroller.current.scrollTop = 0
    } else {
      contactSectionAnims.start('exit')
      contactTitleAnims.start('hidden')
      contactBox1Anims.start('hidden')
      contactBox2Anims.start('hidden')
      contactCopyAnims.start('hidden')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  return (
    <ContactSec
      ref={contactSection}
      initial='hidden'
      animate={contactSectionAnims}
      variants={contactSectionVars}
      transition={{
        opacity: {
          duration: 1,
          ease: 'linear',
        },
        default: {
          duration: 0,
          ease: 'none',
        },
      }}>
      <CustomScroller ref={contactScroller} className='Scroller' innerClassName='ScrollerContent'>
        <ContactWrapper>
          <ContactTitle
            ref={contactTitle}
            initial='hidden'
            animate={contactTitleAnims}
            variants={contactTitleVars}
            transition={{
              duration: 1,
              delay: 0.75,
              ease: 'easeOut',
            }}>
            {contactData.title}
          </ContactTitle>
          <ContactSections>
            <ContactSection
              ref={contactSectionBox1}
              initial='hidden'
              animate={contactBox1Anims}
              variants={contactSect1Vars}
              transition={{
                duration: 1.25,
                delay: 0.75,
                ease: 'easeOut',
              }}>
              <People>
                {Object.entries(people).map(([id, item]) => (
                  <ContactItem key={`people-${id}`} item={item} />
                ))}
              </People>
              <Social className="Social" />
              <Copyright
                ref={contactCopy}
                className="copy"
                initial='hidden'
                animate={contactCopyAnims}
                variants={contactCopyVars}
                transition={{
                  duration: 1.25,
                  delay: 1.25,
                  ease: 'easeOut',
              }}>
                <p>{copy}</p>
                <p>{`${siteData.name} ${siteData.tagline} J-40259095-4`}</p>
            </Copyright>
            </ContactSection>
            <ContactSection
              ref={contactSectionBox2}
              initial='hidden'
              animate={contactBox2Anims}
              variants={contactSect2Vars}
              transition={{
                duration: 1.25,
                delay: 0.75,
                ease: 'easeOut',
              }}>
              {Object.entries(address).map(([id, item]) => (
                <div key={`address-${id}`} className="address">
                  <Address
                    dangerouslySetInnerHTML={{ __html: item.address }}
                  />
                  { item.phone && (
                    <Phone dangerouslySetInnerHTML={{ __html: item.phone }} />
                  )}
                  {item.email && (
                    <Email href={`mailto:${item.email}`}>{item.email}</Email>
                  )}
                  <Map
                    href={item.map}
                    target='_blank'
                    rel='noopener noreferrer'>
                    <span>
                      <Location />
                    </span>
                    { siteData.language === 'es' ? 'Ver en el mapa' : 'See on the map' }
                  </Map>
                </div>
              ))}
            </ContactSection>
            <Copyright
                ref={contactCopy}
                className="copy"
                initial='hidden'
                animate={contactCopyAnims}
                variants={contactCopyVars}
                transition={{
                  duration: 1.25,
                  delay: 1.25,
                  ease: 'easeOut',
              }}>
                <p>{copy}</p>
                <p>{`${siteData.name} ${siteData.tagline} J-40259095-4`}</p>
            </Copyright>
          </ContactSections>
        </ContactWrapper>
      </CustomScroller>
    </ContactSec>
  )
}

Contact.propTypes = {
  visible: PropTypes.bool.isRequired,
}

export default memo(Contact)
